<script lang="ts">
	//Svelte hooks
	import { onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { get_wifi_ios } from '../../../services/native_container_requests_ios';
	import { get_wifi_android } from 'src/services/native_container_requests_android';

	//Stores
	import { saved_wifi_neworks } from 'src/stores/features/wifi/passwords';
	import { user_data } from 'src/stores/account/user';
	import { readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { app, os, wifi_ssid, wifi_ssids } from 'src/stores/session/native-bridge';
	import { update } from 'src/stores/ui/edit_lists';

	//Design system components
	import { Checkbox, Input, Text, Select, Button, Alert } from '@Shelly/shelly-design-system';

	//Helpers
	import { wait } from '../../../helpers/utilities';
	import { getAvailableNetworks } from 'src/helpers/get_gen_2_networks';

	//Types
	import type { Device } from 'src/types/Device';

	//Constants
	import { SLEEPING_TYPES } from 'src/constants';
	import { handleOldWiFiData } from 'src/helpers/inclusion_helpers';
	import { is_gen_2 } from 'src/helpers/interface_helpers';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';

	export let editableObject;

	const isIOS = $os === 'IOS';
	const isAndroid = $app && !isIOS;

	let clearedPassword = false;

	let displayAdvancedNetworks = false;
	let deviceAsScanner: Device;
	let scannedNetworks: { device: Device; rssi: number; count: number } = {
		device: undefined,
		rssi: undefined,
		count: undefined,
	};

	$: checked = editableObject.savePass;

	$: disableToggleVisibility =
		$saved_wifi_neworks !== null &&
		$saved_wifi_neworks.hasOwnProperty($user_data.email) &&
		!clearedPassword;

	const hasGen2 = Object.values($update.selectedDevices).some((device, i) => {
		if (device.gen == 2) {
			return true;
		}
	});

	onMount(async () => {
		handleOldWiFiData();
		if ($app) {
			if ($os === 'IOS') {
				get_wifi_ios(); //getting current wifi network
				await wait(100);
				editableObject.ssid = $wifi_ssids[0];
			} else {
				get_wifi_android(); //getting all discovered networks by phone
				await wait(100);
				editableObject.ssid = $wifi_ssid;
			}
			if (editableObject.ssid.length > 0) {
				editableObject.savePass = true;
			}
		}

		if ($saved_wifi_neworks !== null && $saved_wifi_neworks[$user_data?.email]?.pass) {
			editableObject.pass = $saved_wifi_neworks[$user_data?.email]?.pass;
			editableObject.savePass = true;
		}

		if (!isAndroid && hasGen2) {
			//if device is IOS or Desktop we can't scan for wifi devices. But here we are looping trough all selected for update devices. If there are gen2 devices we are getting their network ssids. Then after sorting by appearances we are getting first device with this ssid and asking it for nearby Wi-Fi networks which can be used with ssid select holder.
			Object.values($update.selectedDevices)
				.filter((d) => {
					if (
						d.gen == 2 &&
						typeof readDeviceStatus(d.id) !== 'undefined' &&
						!d.shared &&
						!SLEEPING_TYPES.includes(d.type)
					) {
						return $devices[d.id];
					}
				})
				.forEach((device) => {
					const deviceStatus = readDeviceStatus(device.id);
					const rssi =
						typeof deviceStatus?.wifi_sta?.rssi !== 'undefined'
							? deviceStatus?.wifi_sta?.rssi
							: deviceStatus?.wifi?.rssi;
					if (rssi > -80)
						scannedNetworks[device.ssid] = {
							rssi,
							device,
							count:
								typeof scannedNetworks[device.ssid] !== 'undefined' &&
								scannedNetworks[device.ssid].hasOwnProperty('count')
									? (scannedNetworks[device.ssid].count += 1)
									: 0,
						};
				});
			deviceAsScanner = Object.values(scannedNetworks).sort((a, b) => b.count - a.count)[0].device; //attempt to sort by appearance so to get device which has more closer devices

			let ssids = Object.values(await getAvailableNetworks(deviceAsScanner)).map(
				(resp) => (resp = resp.value),
			);

			wifi_ssids.set(ssids);
		}
	});

	const handleWifiPassCache = () => {
		//toggler to save or not network password
		checked = !checked;
		editableObject.savePass = checked;
	};

	const clearPass = () => {
		//similar functionality to inclusion process to clear saved password if ssid was changed
		if (
			$saved_wifi_neworks &&
			editableObject?.ssid !== $saved_wifi_neworks[$user_data?.email]?.ssid
		) {
			editableObject.pass = '';
		}
	};

	const handleWiFiSsidChange = (e) => {
		editableObject.ssid = e.detail;
		clearPass();
	};

	const clearToggleVisibility = () => {
		//if password is cleared the password eye became active. Other wise we are hiding already saved password
		if (editableObject.pass.length === 0) {
			clearedPassword = true;
		}
	};

	$: ipv4DeviceStatuses = Object.keys($update.selectedDevices).map((id) => {
		const settings = readDeviceSettings(id);
		const isGen2 = is_gen_2($devices[id]);
		if (editableObject.sta === 0) {
			return isGen2 ? settings?.wifi?.sta?.ipv4mode : settings?.wifi_sta?.ipv4_method;
		} else {
			return isGen2 ? settings?.wifi?.sta1?.ipv4mode : settings?.wifi_sta1?.ipv4_method;
		}
	});
</script>

<Text element="h2" class="mb-s"
	>{$format('wifi_credentials_desc', {
		default: 'Enter desired Wi-Fi network credentials.',
	})}</Text
>
<Text class="mb-s" styleType="body-small">
	{$format('inclusion_wifi_credentials_description', {
		default:
			'Please enter your personal Wi-Fi network credentials. The device will be included in this network.',
	})}
</Text>
{#if displayAdvancedNetworks}
	<Select
		class="mb-s"
		size="l"
		value={editableObject.ssid}
		label="ssid"
		icon="fas fa-wifi"
		on:change={handleWiFiSsidChange}
		options={$wifi_ssids}
		id="select-au1nfgh"
		aria-labelledby="select-au1nfgh"
	/>
{:else}
	<Input
		size="l"
		class="mb-s"
		label="ssid"
		bind:value={editableObject.ssid}
		on:input={clearPass}
		icon="fas fa-wifi"
		id="input-xf9cb7o"
		aria-labelledby="input-xf9cb7o"
	/>
{/if}

<Input
	a11yProps={{
		iconButtonAriaLabel: $format('input_wifi_password', { default: 'Input Wi-Fi Password' }),
	}}
	size="l"
	class="mb-s"
	label="*******"
	bind:value={editableObject.pass}
	type="password"
	icon="fas fa-lock-keyhole"
	disabled={editableObject.isOpen}
	{disableToggleVisibility}
	on:input={clearToggleVisibility}
	id="input-beowpio"
	aria-labelledby="input-beowpio"
/>
<Checkbox
	bind:checked={editableObject.savePass}
	element="h2"
	on:click={handleWifiPassCache}
	id="checkbox-icqentd"
	>{$format('label_save_password', {
		default: 'remember password',
	})}</Checkbox
>
<Checkbox element="h2" bind:checked={editableObject.isOpen} id="checkbox-0k4fz78"
	>{$format('wifi_open_network', { default: 'Open network' })}</Checkbox
>

{#if isDevServerUser(['repo', 't1', 'nb'])}
	{#if (!isAndroid && hasGen2) || isAndroid}
		<Button
			size="m"
			width="full"
			variant="secondary"
			class="mt-s"
			on:click={() => {
				displayAdvancedNetworks = !displayAdvancedNetworks;
			}}
			>{$format('show_available_networks', {
				default: 'Show available networks',
			})}</Button
		>
	{/if}

	<!-- <DeviceWiFiScanSelection This is device picker with purpose to choose which device to be used to scan for nearby Wi-Fi networks 
		bind:showDeviceSelection
		bind:editableObject
		bind:selectedDevices
	/> -->
{/if}

{#if ipv4DeviceStatuses.includes('static')}
	<Alert
		class="mt-s"
		type="info"
		heading={$format('text_note_ip_address_status', {
			default:
				'Please note that if any of the selected devices are configured to use a static IP address, they will revert to DHCP mode after the update.',
		})}
	/>
{/if}
